import { useTranslation } from 'next-i18next';

import { NS } from '@/constants';

import { AddressModel } from '@/models';
import { CallToActionKind } from '@/types';

import { useAppDispatch, useAppSelector } from '@/redux';
import { selectBillingAddress, selectShippingAddress } from '@/redux/slices/cart.slice';
import { selectCurrentStepLabel } from '@/redux/slices/checkout.slice';
import { toggleAddressFormModal } from '@/redux/slices/expro.slice';
import { showConfirmModal } from '@/redux/slices/gui.slice';
import { deleteUserAddress, selectUserAddresses } from '@/redux/slices/user.slice';
import { updateCartAttributeByName } from '@/redux/thunks/checkout/updateCartAttributeByName';

import { useScreenSizeContext } from '@/components/ScreenSize.context';
import { getCountryLabel } from '@/utils/addressUtils';

import RadioButton from '@/components/common/RadioButton';
import WarningInformation from '@/components/common/WarningInformation';
import { useServices } from '@/services/useServices';
import CallToActionContainer from '@/components/common/callToAction/CallToActionContainer';

import styles from './AddressSection.module.scss';
import { CUSTOM_PAGE_VIEW_EVENT, CUSTOM_PAGE_VIEW_TYPE } from '@/constants/datalayer';
import { URLS } from '@/constants/urls';

export const AddressSection = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const userAddresses = useAppSelector(selectUserAddresses);
  const shippingAddress = useAppSelector(selectShippingAddress);
  const billingAddress = useAppSelector(selectBillingAddress);
  const currentStep = useAppSelector(selectCurrentStepLabel);
  const isMobile = useScreenSizeContext().isMobile;
  const { datalayerService } = useServices();

  const defaultAddress = userAddresses?.find((address) => address.isDefault) || userAddresses?.[0] || {} as AddressModel;
  const effectiveAddress = shippingAddress || defaultAddress;
  const activeAddress = currentStep === 'billing' ? billingAddress : effectiveAddress;

  const checkIfIsActiveAddress = (addressId: string) => (
    [
      styles.container,
      addressId === activeAddress?.idNext ? styles['active-card'] : ''
    ].join(' ')
  );

  const changeAddress = (address: AddressModel) => {
    const isBillingMode = currentStep === 'billing';
    dispatch(updateCartAttributeByName({ attributeName: isBillingMode ? 'billing' : 'shipping', newValue: address }));
  };

  const addAddress = () => {
    dispatch(toggleAddressFormModal({ opened: true }));
    datalayerService.pushPageName(
      CUSTOM_PAGE_VIEW_EVENT.CS_ADDRESS_POPIN_OPEN_PV,
      CUSTOM_PAGE_VIEW_TYPE.CHECKOUT_CREATE_ADDRESS
    );
  };

  const editAddress = (e: MouseEvent, addressToEdit: AddressModel) => {
    dispatch(toggleAddressFormModal({ opened: true, addressToEdit }));
    datalayerService.pushPageName(
      CUSTOM_PAGE_VIEW_EVENT.CS_ADDRESS_POPIN_OPEN_PV,
      CUSTOM_PAGE_VIEW_TYPE.CHECKOUT_EDIT_ADDRESS
    );
    e.stopPropagation();
  };

  const deleteAddress = (e: MouseEvent, addressId: string) => {
    dispatch(showConfirmModal({
      content: {
        title: t('account.address.removeAdress'),
        content: t('account.address.removeAdressContent'),
        cancel: t('btn.cancel'),
        confirm: t('btn.delete'),
        confirmClick: () => dispatch(deleteUserAddress({ addressId })),
        fullScreen: isMobile
      }
    }));
    e.stopPropagation();
  };

  return (
    <div className={styles['address-section-container']}>
      <CallToActionContainer
        onClick={addAddress}
        icon={{ url: URLS.ADD_BLACK, alt: t('plusIcon', { ns: NS.ALTER }) }}
        fullWidth
        kind={CallToActionKind.SECONDARY}
        id="add-address"
        className={styles['add-address-button']}
      >
        {t('btn.addAddr')}
      </CallToActionContainer>
      {userAddresses?.length > 0 ? (
        <div className={styles['address-container']}>
          {userAddresses?.map((address) => (
            <div key={address.id} className={checkIfIsActiveAddress(address.idNext)} onClick={() => changeAddress(address)}>
              <RadioButton
                id={`${address.id}-radio`} isChecked={activeAddress?.idNext === address.idNext}
                customClassName={styles['radio-button']}
              />
              <section className={styles.address}>
                <section className={styles['address-info']}>
                  <p>{address.firstName} {address.lastName}</p>
                  {address.company && (
                    <p>{address.company}</p>
                  )}
                  <p>{address.street}</p>
                  <p>{address.postalCode}, {address.city}</p>
                  <p>{getCountryLabel(address.country)}</p>
                  <p>{address.phoneNumberMobile}</p>
                </section>
                <div className={styles.actions}>
                  <CallToActionContainer
                    kind={CallToActionKind.GHOST}
                    onClick={(e: MouseEvent) => editAddress(e, address)}
                  >
                    {t('btn.modify')}
                  </CallToActionContainer>
                  <CallToActionContainer
                    kind={CallToActionKind.GHOST}
                    onClick={(e: MouseEvent) => deleteAddress(e, address.idNext)}
                  >
                    {t('btn.delete')}
                  </CallToActionContainer>
                </div>
              </section>
            </div>
          ))}
        </div>
      ) : (
        <WarningInformation title={t('account.noAddress')} />
      )}
    </div>
  );
};
